import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users,
  Database,
  Mail,
  FileImage,
  MessageCircle,
  Activity,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink
} from 'lucide-react';

interface DashboardStats {
  userStats: {
    totalUsers: number;
    activeUsers: number;
    blockedUsers: number;
    adminUsers: number;
    totalFilesUploaded: number;
    totalAiChats: number;
  };
  activityStats: Array<{
    _id: string;
    count: number;
    lastActivity: string;
  }>;
  supportStats: {
    totalRequests: number;
    openRequests: number;
    inProgressRequests: number;
    resolvedRequests: number;
    closedRequests: number;
  };
  recentActivity: Array<{
    _id: string;
    userId: string;
    userEmail: string;
    action: string;
    details: any;
    createdAt: string;
  }>;
  systemStatus: {
    maintenanceMode: boolean;
    serverTime: string;
  };
}

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboardAnalytics();
      setStats(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse bg-card/50 border-border/50">
              <CardContent className="p-6">
                <div className="h-4 bg-muted/50 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-muted/50 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="bg-red-50 border-red-200">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-red-800">
          {error}
        </AlertDescription>
      </Alert>
    );
  }

  if (!stats) {
    return null;
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats.userStats.totalUsers,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Active Users',
      value: stats.userStats.activeUsers,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },

    {
      title: 'Files Uploaded',
      value: stats.userStats.totalFilesUploaded,
      icon: FileImage,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      title: 'AI Chats',
      value: stats.userStats.totalAiChats,
      icon: MessageCircle,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    },
    {
      title: 'Support Requests',
      value: stats.supportStats.totalRequests,
      icon: Activity,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'Blocked Users',
      value: stats.userStats.blockedUsers,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'login': 'User Login',
      'logout': 'User Logout',
      'signup': 'New Signup',

      'file_uploaded': 'File Uploaded',
      'ai_chat': 'AI Chat',
      'account_blocked': 'Account Blocked',
      'account_unblocked': 'Account Unblocked',
      'role_changed': 'Role Changed'
    };
    return labels[action] || action.replace('_', ' ').toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* System Status */}
      {stats.systemStatus.maintenanceMode && (
        <Alert className="bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-yellow-800 dark:text-yellow-200">
            System is currently in maintenance mode. Users are locked out.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="group hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-card via-card to-card/80 border-border/50 hover:border-primary/20 hover:scale-105 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <CardContent className="p-6 relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground group-hover:text-foreground/80 transition-colors">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-foreground mt-2 group-hover:scale-105 transition-transform">
                    {formatNumber(stat.value)}
                  </p>
                </div>
                <div className={`p-4 rounded-2xl ${stat.bgColor} bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                  <stat.icon className={`h-7 w-7 ${stat.color} drop-shadow-sm`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="h-5 w-5" />
              <span>Recent Activity</span>
            </CardTitle>
            <CardDescription>
              Latest user actions and system events
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.recentActivity.slice(0, 10).map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-card border border-border rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Activity className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {getActionLabel(activity.action)}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {activity.userEmail}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(activity.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
              {stats.recentActivity.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  No recent activity
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Support Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5" />
                <span>Support Overview</span>
              </div>
              <Link to="/admin/support">
                <Button variant="outline" size="sm" className="text-xs">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View All
                </Button>
              </Link>
            </CardTitle>
            <CardDescription>
              Current support request status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-900">Open</span>
                </div>
                <span className="text-lg font-bold text-red-900">
                  {stats.supportStats.openRequests}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-900">In Progress</span>
                </div>
                <span className="text-lg font-bold text-yellow-900">
                  {stats.supportStats.inProgressRequests}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900">Resolved</span>
                </div>
                <span className="text-lg font-bold text-green-900">
                  {stats.supportStats.resolvedRequests}
                </span>
              </div>

              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Requests</span>
                  <span className="font-semibold">
                    {stats.supportStats.totalRequests}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
